import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Box from "@mui/material/Box";
import { Chip, ConfirmDialog, DataTable, Tooltip } from "@outpave/ds";
import type { DataColumn, RowAction } from "@outpave/ds";
import { Inline } from "components/layout/reusableLayouts";
import useInfiniteScroll from "hooks/useInfiniteScroll";
import LoadingWithoutModal from "components/common/LoadingWithoutModal";
import { CustomFieldFilterProps } from "pages/admin/CustomFields";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  ListViewIcon,
  TypeCursorIcon,
  CursorCircleSelection01Icon,
  CheckmarkCircle04Icon,
  ViewIcon,
  CancelCircleIcon,
  CheckmarkCircle02Icon,
  ViewOffSlashIcon,
} from "@hugeicons-pro/core-stroke-rounded";

import useCustomFieldEditor from "hooks/useCustomFieldEditor";
import CustomFieldParentType from "enums/CustomFieldParentType";
import CustomFieldStatus from "enums/CustomFieldStatus";
import CustomFieldType from "enums/CustomFieldType";
import {
  CustomFieldParentTypeDisplay,
  CustomFieldTypeDisplay,
} from "hooks/customFields.types";
import { GET_CUSTOM_FIELD_SETTINGS } from "graphql/customFieldSetting";
import { DEFAULT_PAGINATION_SIZE } from "components/common/Constants";
import { useDynamicConfirmModal } from "hooks/useDynamicConfirmModal";

type Data = {
  id: string;
  label: string;
  required: boolean;
  type: CustomFieldType;
  parentType: CustomFieldParentType[];
  status: CustomFieldStatus;
  order: number;
};

type CustomFieldTableProps = {
  handleOpenDrawer: (fieldId: string) => void;
  filterCriteria: CustomFieldFilterProps;
  refreshCustomFieldSettings?: number;
};

const CustomFieldSettingsTable: React.FC<CustomFieldTableProps> = (props) => {
  const { handleOpenDrawer, filterCriteria, refreshCustomFieldSettings } =
    props;

  const { t } = useTranslation();
  const [defaultPaginationVariables, setDefaultPaginationVariables] = useState(
    {},
  );

  useEffect(() => {
    if (filterCriteria && Object.keys(filterCriteria).length > 0) {
      setDefaultPaginationVariables({
        first: DEFAULT_PAGINATION_SIZE,
        after: undefined,
        status: filterCriteria.status,
      });
    }
  }, [filterCriteria]);

  const extractCustomFieldSettings = (data: any) => {
    const firstKey = Object.keys(data)[0];
    return data[firstKey]?.edges.map((item: any) => ({
      id: item.node.id,
      label: item.node.label,
      required: item.node.isRequired,
      type: item.node.type,
      parentType: item.node.parentTypes,
      status: item.node.status,
      order: item.node.order,
    }));
  };

  const filterCustomFields = (item: any) => {
    return item.status === filterCriteria.status;
  };

  const { dataArray, displayLoading, handleScroll, isInfiniteScrollFetching } =
    useInfiniteScroll({
      query: GET_CUSTOM_FIELD_SETTINGS,
      initialVariables: defaultPaginationVariables,
      extractData: extractCustomFieldSettings,
      filterFunction: filterCustomFields,
      refetchIndex: refreshCustomFieldSettings,
    });

  const { isAnyLoading, handleFieldStatusActionWithModal } =
    useCustomFieldEditor({});
  const {
    dynamicConfirmModal,
    openDynamicConfirmModal,
    closeDynamicConfirmModal,
  } = useDynamicConfirmModal();

  const handleReactivateField = (fieldId: string) => {
    handleFieldStatusActionWithModal(
      CustomFieldStatus.ACTIVE,
      fieldId,
      openDynamicConfirmModal,
    );
  };

  const handleReadOnlyField = (fieldId: string) => {
    handleFieldStatusActionWithModal(
      CustomFieldStatus.READONLY,
      fieldId,
      openDynamicConfirmModal,
    );
  };

  const handleDisableField = (fieldId: string) => {
    handleFieldStatusActionWithModal(
      CustomFieldStatus.DISABLED,
      fieldId,
      openDynamicConfirmModal,
    );
  };

  const columns: DataColumn<Data>[] = useMemo(
    () => [
      {
        id: "order",
        header: t("Order"),
        cell: (field) => field.order,
      },
      {
        id: "label",
        header: t("Label"),
        cell: (field) => field.label,
      },
      {
        id: "required",
        header: t("Required"),
        align: "center",
        // A required field is a constraint worth spotting down the column, so it
        // takes the attention tone; "No" is the unremarkable case, not a success.
        cell: (field) => (
          <Chip size="sm" tone={field.required ? "warning" : "neutral"}>
            {field.required ? t("Yes") : t("No")}
          </Chip>
        ),
      },
      {
        id: "type",
        header: t("Type"),
        align: "center",
        cell: (field) => <RenderCustomFieldType type={field.type} />,
      },
      {
        id: "parentType",
        header: t("Used In"),
        align: "center",
        cell: (field) => <RenderParentTypes parentTypes={field.parentType} />,
      },
    ],
    [t],
  );

  const rowActions = (field: Data): RowAction[] => [
    {
      label: t("View Field Details"),
      icon: <HugeiconsIcon icon={ViewIcon} size={16} />,
      onClick: () => handleOpenDrawer(field.id),
    },
    ...(field.status === CustomFieldStatus.ACTIVE
      ? []
      : [
          {
            label: t("Set Field Active"),
            icon: <HugeiconsIcon icon={CheckmarkCircle02Icon} size={16} />,
            onClick: () => handleReactivateField(field.id),
          },
        ]),
    ...(field.status === CustomFieldStatus.READONLY
      ? []
      : [
          {
            label: t("Set Field Read Only"),
            icon: <HugeiconsIcon icon={ViewOffSlashIcon} size={16} />,
            onClick: () => handleReadOnlyField(field.id),
          },
        ]),
    ...(field.status === CustomFieldStatus.DISABLED
      ? []
      : [
          {
            label: t("Disable Field"),
            icon: <HugeiconsIcon icon={CancelCircleIcon} size={16} />,
            onClick: () => handleDisableField(field.id),
          },
        ]),
  ];

  return (
    <Box sx={{ position: "relative", height: "100%" }}>
      <DataTable<Data>
        columns={columns}
        rows={dataArray}
        getRowKey={(field) => field.id}
        loading={displayLoading}
        isFetchingMore={isInfiniteScrollFetching}
        onRowClick={(field) => handleOpenDrawer(field.id)}
        onScroll={handleScroll}
        emptyState={t("No custom fields found")}
        rowActions={rowActions}
        rowActionsLabel={t("RowActions")}
        loadingMoreText={t("Loading more custom fields")}
        stickyHeader
      />
      <LoadingWithoutModal open={isAnyLoading} />
      <ConfirmDialog
        open={dynamicConfirmModal.open}
        tone="primary"
        title={dynamicConfirmModal.title}
        message={dynamicConfirmModal.description}
        confirmLabel={t("Yes")}
        cancelLabel={t("No")}
        onConfirm={() => {
          dynamicConfirmModal.onConfirm();
          closeDynamicConfirmModal();
        }}
        onCancel={closeDynamicConfirmModal}
      />
    </Box>
  );
};

export default CustomFieldSettingsTable;

const MAX_VISIBLE_PARENT_TYPES = 3;

const RenderParentTypes: React.FC<{
  parentTypes: CustomFieldParentType[];
}> = ({ parentTypes }) => {
  const { t } = useTranslation();
  const displayOrder = Object.keys(CustomFieldParentTypeDisplay);
  const sorted = [...parentTypes].sort(
    (a, b) => displayOrder.indexOf(a) - displayOrder.indexOf(b),
  );

  const visible = sorted.slice(0, MAX_VISIBLE_PARENT_TYPES);
  const hidden = sorted.slice(MAX_VISIBLE_PARENT_TYPES);

  return (
    <Inline gap="var(--space-2)" justify="center" wrap={false}>
      {visible.map((item, index) => (
        <Chip key={index} size="sm" tone="neutral">
          {t(CustomFieldParentTypeDisplay[item])}
        </Chip>
      ))}
      {hidden.length > 0 && (
        <Tooltip
          title={hidden
            .map((item) => t(CustomFieldParentTypeDisplay[item]))
            .join(", ")}
        >
          <Chip size="sm">{`+${hidden.length}`}</Chip>
        </Tooltip>
      )}
    </Inline>
  );
};

const RenderCustomFieldType: React.FC<{ type: CustomFieldType }> = ({
  type,
}) => {
  const { t } = useTranslation();

  const typeIconMap: { [key in CustomFieldType]: React.ReactElement } = {
    [CustomFieldType.TEXT]: <HugeiconsIcon icon={TypeCursorIcon} size={14} />,
    [CustomFieldType.CHECKBOX]: (
      <HugeiconsIcon icon={CheckmarkCircle04Icon} size={14} />
    ),
    [CustomFieldType.LIST]: <HugeiconsIcon icon={ListViewIcon} size={14} />,
    [CustomFieldType.RADIO]: (
      <HugeiconsIcon icon={CursorCircleSelection01Icon} size={14} />
    ),
    [CustomFieldType.ALLOCATION]: (
      <HugeiconsIcon icon={ListViewIcon} size={14} />
    ),
  };

  return (
    <Chip key={type} size="sm" leading={typeIconMap[type]}>
      {t(CustomFieldTypeDisplay[type])}
    </Chip>
  );
};
